<!-- Copyright: Seán I. O'Donoghue -->
<!-- A poster's own page, /Posters/<year>/<poster> (2026-10-26, redesigned through the day): one -->
<!-- header, then the whole poster right below it (PosterViewer.vue). Closed, the header is one -->
<!-- line (←, "VIZBI 2025 Poster", the title and authors cut short, ⌄); open -->
<!-- (#details), the title, authors and abstract unfold above, below or beside the poster, as -->
<!-- shows it largest (posterLayout.js); the page as wide as that needs. Files: the old server. -->
<template>
  <div ref="page" class="posterPage" :style="{width: pageWidth ? `${pageWidth}px` : null}">
    <p v-if="!posters.length" class="status">Loading the poster…</p>
    <p v-else-if="!poster" class="status">There's no poster {{ posterNum }} in {{ year }}.</p>
    <!-- one grid on one surface: the toolbar across the top; below it the headline (title,
         authors, addresses), the poster and the body (abstract, references), as the layout
         places them (posterLayout.js: stacked, side, full, columns). A click anywhere on it
         opens and closes Details, but on a link, a button or the image (full screen) -->
    <article
      v-else
      class="poster"
      :class="[layout, {isOpen: isDetailsOpen, hasFooterRow: isFooterRow}]"
      :style="{
        '--text-column': `${textColumn}px`,
        '--gutter': `${GUTTER}px`,
        '--inset': `${INSET}px`,
        '--text-pad': `${TEXT_PAD}px`,
        '--column-gap': `${COLUMN_GAP}px`,
        '--image-top': `${imageTop}px`,
        '--min-height': `${minHeight}px`,
      }"
      :title="toggleHint"
      @click="onSurfaceClick">
      <header ref="header" class="header">
        <div class="toolbar">
          <a
            :href="collectionPath"
            class="back"
            aria-label="Back to posters"
            title="Back to posters"
            @click.prevent="goBack">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path :d="BACK_ICON" /></svg>
          </a>
          <!-- one line: the collection in green, then (closed) the title and authors, cut
               short together (…); open, they're in full below, and the collection moves beside
               Back (plain text in both: Seán, 2026-10-26) -->
          <p class="heading">
            <span ref="collectionSlot" class="collection">{{ collection }}</span>
            <span
              v-show="!isDetailsOpen"
              class="text"
              :title="`${fullHeading}\n\nClick to show details`">
              <span class="title">{{ poster.title }}</span>{{ " " }}
              <span class="authors">{{ poster.authors }}</span>
            </span>
          </p>
          <!-- Details for the keyboard too (Enter and Space: a button) -->
          <button
            type="button"
            class="detailsChevron"
            :aria-expanded="isDetailsOpen"
            aria-controls="posterDetails posterAbstract"
            :aria-label="isDetailsOpen ? 'Hide details' : 'Show details'"
            :title="toggleHint"
            @click="toggleDetails">
            <ArrowUp v-if="isDetailsOpen" aria-hidden="true" />
            <ArrowDown v-else aria-hidden="true" />
          </button>
        </div>
      </header>
      <!-- Details' headline: unfolding in place (its height eased open and shut); as a paper's:
           the title, the authors and their addresses -->
      <div id="posterDetails" class="expansion" :inert="!isDetailsOpen">
        <div ref="headline" class="expansionInner">
          <h1 class="fullTitle">{{ poster.title }}</h1>
          <p class="fullAuthors">{{ poster.authors }}</p>
          <p v-if="poster.address" class="affiliation">{{ poster.address }}</p>
        </div>
      </div>
      <div ref="viewerSlot" class="viewerSlot">
        <PosterViewer
          v-if="viewerHeight"
          :poster="poster"
          :width="viewerWidth"
          :height="viewerHeight"
          :download-url="downloadUrl" />
      </div>
      <!-- the abstract: unfolding below the poster, or beside it -->
      <div id="posterAbstract" class="body" :inert="!isDetailsOpen">
        <div ref="bodyInner" class="bodyInner">
          <p v-if="details?.abstract?.length" class="abstract">
            <template v-for="(part, index) in details.abstract" :key="index">
              <a v-if="part.href" :href="part.href" title="">{{ part.text }}</a>
              <template v-else>{{ part.text }}</template>
            </template>
          </p>
        </div>
      </div>
      <!-- the links, at the bottom of the page (stretched to the window: its last row but one
           takes the rest): one under the other, or a row (the author's left, the talk centred,
           this poster's address right) -->
      <div class="footer" :inert="!isDetailsOpen">
        <div class="footerInner">
          <!-- the author's website; then VIZBI's own: this poster (green: here, not a link) and
               its talk (a link), poster/2025/A09 · lightning_talk/2025/A09 -->
          <div ref="references" class="references" :class="{isRow: isFooterRow}">
            <a v-if="details?.link" :href="details.link" class="link" title="">{{ linkText }}</a>
            <span class="ours">
              <span class="current" aria-current="page">{{ currentLabel }}</span>
              <template v-if="talkUrl">
                <span class="dot" aria-hidden="true">·</span>
                <a
                  :href="talkUrl"
                  class="talk"
                  data-same-tab
                  title="Lightning Talk"
                  @click.prevent="openOnOldServer(talkUrl)">
                  lightning_talk/{{ year }}/{{ posterNum }}
                </a>
              </template>
            </span>
          </div>
        </div>
      </div>
    </article>
  </div>
</template>

<script setup>
import {
  computed, nextTick, onActivated, onBeforeUnmount, onMounted, ref, watch, watchEffect,
} from "vue";
import {useRoute, useRouter} from "vue-router";
import PosterViewer from "@/components/PosterViewer.vue";
import ArrowUp from "@/assets/img/ArrowUp.vue";
import ArrowDown from "@/assets/img/ArrowDown.vue";
import {fetchPosterDetails, getPosterFileUrl, usePosters} from "@/composables/usePosters.js";
import {useDisplayWidth} from "@/composables/useDisplayWidth.js";
import {
  COLUMN_GAP, GUTTER, INSET, MIN_IMAGE_HEIGHT, TEXT_BLOCK, TEXT_PAD, getKey, getPosterLayout,
} from "@/composables/posterLayout.js";

const props = defineProps({
  year: {type: String, required: true},
  posterNum: {type: String, required: true} // e.g. Y14
});

const OLD_SITE = "https://www.vizbi.org";
const BACK_ICON = "M20 11H7.8l5.6-5.6L12 4l-8 8 8 8 1.4-1.4L7.8 13H20z";
const DETAILS = "#details";
const route = useRoute();
const router = useRouter();
const {posters} = usePosters();
const [page, header, headline, bodyInner, viewerSlot] = [ref(), ref(), ref(), ref(), ref()];
const [references, isFooterRow, imageTop, minHeight] = [ref(), ref(false), ref(0), ref(0)];
const [details, viewerWidth, viewerHeight] = [ref(null), ref(0), ref(0)];
const [layout, textColumn, contentWidth, closedWidth] = [ref("stacked"), ref(0), ref(0), ref(0)];
let layoutKey = ""; // the layout and its text column now (posterLayout.js: kept on a resize)
// the page's width: its layout's open, or (closed) the poster's with room for the toolbar
const pageWidth = computed(() => (isDetailsOpen.value ? contentWidth.value : closedWidth.value));
const displayWidth = useDisplayWidth(); // the site's width class: "narrow" below 480px
const poster = computed(() => posters.value.find((each) => {
  return each.year === props.year && each.posterNum === props.posterNum;
}));
const ART = "Art & Biology";
const isArtwork = computed(() => poster.value.category === ART);
// which VIZBI and what: "VIZBI 2025 Poster", or "… Artwork" for Art & Biology (plain text: a
// click on it opens and closes Details, as on the rest of the row)
const collection = computed(() => {
  return `VIZBI ${poster.value.year} ${isArtwork.value ? "Artwork" : "Poster"}`;
});
// this poster, as a path: poster/2025/A09, artwork/2023/Y14 (its address, to cite it by, is
// vizbi.org/poster/2025/A09: router/index.js)
const currentLabel = computed(() => {
  return `${isArtwork.value ? "artwork" : "poster"}/${props.year}/${props.posterNum}`;
});
// the author's link as read, without its protocol: www.nature.com/articles/…
const linkText = computed(() => String(details.value?.link || "").replace(/^https?:\/\//i, ""));
const fullHeading = computed(() => `${poster.value.title} — ${poster.value.authors}`);
// the surface's tooltip, saying what a click there does (its links, buttons and the image have
// their own; an empty one stops a link inheriting this)
const toggleHint = computed(() => {
  return isDetailsOpen.value ? "Hide details" : "Show details";
});
const downloadUrl = computed(() => {
  return getPosterFileUrl(poster.value, details.value?.hasGif ? "gif" : "png");
});
const collectionPath = computed(() => `/Posters/${props.year}`);

// the Lightning Talk, from 2014 on (the old page's rule); not Art & Biology's slide show (Y)
const talkUrl = computed(() => {
  if (Number(props.year) < 2014 || /^Y/i.test(props.posterNum)) return "";
  return `${OLD_SITE}/Lightning/${props.year}/${props.posterNum}`;
});

// Details is the address's #details: shareable, and Back and Forward open and close it.
// Opening adds an entry; closing goes back over it when it was ours, else drops the hash
const isDetailsOpen = computed(() => route.hash === DETAILS);
function toggleDetails() {
  if (!isDetailsOpen.value) return router.push({hash: DETAILS});
  const previous = String(window.history.state?.back || "");
  if (previous === route.path) return router.back();
  router.replace({hash: ""});
}

// the collection's move between centred (closed) and beside Back (open), eased as Details
// unfolds: where it was, measured before the page changes, then slid from there to its new place
const UNFOLD_MS = 350;
const collectionSlot = ref();
function slideCollection(fromLeft) {
  const element = collectionSlot.value;
  if (!element || matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const shift = fromLeft - element.getBoundingClientRect().left;
  if (!shift) return;
  element.style.transition = "none";
  element.style.transform = `translateX(${shift}px)`;
  element.getBoundingClientRect(); // the start in place before the easing
  element.style.transition = `transform ${UNFOLD_MS}ms ease`;
  element.style.transform = "";
}
watch(isDetailsOpen, () => {
  const fromLeft = collectionSlot.value?.getBoundingClientRect().left;
  if (fromLeft !== undefined) nextTick(() => slideCollection(fromLeft));
  startCapEasing();
}, {flush: "pre"});

// open, the image no taller than its layout chose (posterLayout.js: shrunk so the abstract
// fits, Seán, 2026-10-26); the cap eased from the image's height to it as Details unfolds
// (and to the window's height as it folds), so the poster never jumps
const chosenHeight = ref(Infinity);
const cap = {from: Infinity, start: 0};
function startCapEasing() {
  [cap.from, cap.start] = [viewerHeight.value || window.innerHeight, performance.now()];
}
function getImageCap() {
  const target = isDetailsOpen.value ? chosenHeight.value : window.innerHeight;
  const isReduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const progress = isReduced ? 1 : Math.min(1, (performance.now() - cap.start) / UNFOLD_MS);
  if (progress < 1) requestAnimationFrame(updateViewerHeight);
  const eased = progress * (2 - progress); // ease-out
  return progress < 1 ? cap.from + (target - cap.from) * eased : target;
}

// a click on the surface, but on a link or button (each its own action, the chevron's Details),
// on the image (full screen) or after selecting text. By the click's path as dispatched: full
// screen moves the image out of the page while the click is still on its way up
const OWN_ACTIONS = "a, button, .viewerBox";
function onSurfaceClick(event) {
  const hasOwnAction = event.composedPath().some((element) => element.matches?.(OWN_ACTIONS));
  if (hasOwnAction || String(window.getSelection())) return;
  toggleDetails();
}

// back to the gallery as it was (kept alive: its filters and scroll), past any #details
// entries opened here; else (a link straight here) the poster's year
let galleryPosition = null; // the history position of the gallery this page was opened from
function noteGallery() {
  const {back, position} = window.history.state || {};
  const isFromGallery = /^\/Posters(\/\d{4})?(\?|$)/.test(String(back || ""));
  if (route.hash !== DETAILS) galleryPosition = isFromGallery ? position - 1 : null;
}
function goBack() {
  const position = window.history.state?.position;
  if (galleryPosition !== null && position > galleryPosition) {
    return router.go(galleryPosition - position);
  }
  router.push(collectionPath.value);
}
// our own old server, in the same tab (CLAUDE.md § Navigation conventions)
const openOnOldServer = (url) => (window.location.href = url);

// the poster's width ÷ height: its full size, else its thumbnail's (posters.json)
const getAspect = ({pyramid, width, height}) => (pyramid ? pyramid.width / pyramid.height
  : width / height);

// the image's box, the poster's own fitted size: right below the header (closed or open), the
// screen's rest (at least MIN_IMAGE_HEIGHT: below a tall header the page scrolls to it), and no
// taller than the poster at its column's width. Not while hidden: in full screen OpenSeadragon
// hides the page, which measures 0 wide; a height of 0 would unmount the viewer and leave full
// screen
function updateViewerHeight() {
  if (!viewerSlot.value || !poster.value) return;
  const {top, width: slotWidth} = viewerSlot.value.getBoundingClientRect();
  if (!slotWidth) return;
  const {paddingLeft, paddingRight, paddingTop} = getComputedStyle(viewerSlot.value);
  const width = slotWidth - parseFloat(paddingLeft) - parseFloat(paddingRight); // the surface
  const imageTopNow = top + parseFloat(paddingTop) + window.scrollY;
  const available = window.innerHeight - imageTopNow - INSET;
  const height = Math.min(Math.max(MIN_IMAGE_HEIGHT, available), getImageCap());
  const aspect = getAspect(poster.value);
  viewerHeight.value = Math.floor(Math.min(height, width / aspect));
  viewerWidth.value = Math.min(Math.floor(width), Math.round(viewerHeight.value * aspect));
}

// a text's height at a width (its content's: padding apart), set on it for the moment of
// measuring (no paint between); its full height though folded
function measureHeight(element, width) {
  Object.assign(element.style, {width: `${width}px`, maxWidth: "none"});
  const {paddingTop, paddingBottom} = getComputedStyle(element);
  const height = element.scrollHeight - parseFloat(paddingTop) - parseFloat(paddingBottom);
  Object.assign(element.style, {width: "", maxWidth: ""});
  return height;
}
const measureHead = (width) => measureHeight(headline.value, width);
// the abstract in one column or two (as the columns layout), for the moment of measuring
function measureBody(width, columns) {
  const abstract = bodyInner.value.querySelector(".abstract");
  const columnGap = `${COLUMN_GAP}px`;
  if (abstract) Object.assign(abstract.style, {columnCount: String(columns), columnGap});
  const height = measureHeight(bodyInner.value, width);
  if (abstract) Object.assign(abstract.style, {columnCount: "", columnGap: ""});
  return height;
}
// a character's average width in the abstract's font, from its own text (until it arrives,
// ordinary English); not 1ch, the width of "0", wider than most letters: 70ch was about 86
const SAMPLE = "Visualizing biological data helps researchers see patterns in genomes, cells and "
  + "proteins, and share what they find with colleagues, students and the public.";
function measureCharacter() {
  const abstract = (details.value?.abstract || []).map(({text}) => text).join("");
  const ruler = document.createElement("span");
  ruler.style.cssText = "position: absolute; visibility: hidden; white-space: nowrap";
  ruler.textContent = abstract.length > SAMPLE.length ? abstract : SAMPLE;
  bodyInner.value.append(ruler);
  const width = ruler.getBoundingClientRect().width / ruler.textContent.length;
  ruler.remove();
  return width;
}
// the gap from a line's top to its capitals' (the font's own measures, from a canvas):
// half the leading, and the ascent above the capitals
function getCapOffset(element) {
  const style = getComputedStyle(element);
  const context = document.createElement("canvas").getContext("2d");
  context.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  const metrics = context.measureText("H");
  const content = metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent;
  const line = style.lineHeight === "normal" ? content : parseFloat(style.lineHeight);
  return (line - content) / 2 + metrics.fontBoundingBoxAscent - metrics.actualBoundingBoxAscent;
}
// where the image's top goes, below its row's: level with the title's capitals (full: the
// title's top margin, then its first line) or the abstract's (side: the text's padding)
function getImageTops() {
  const title = headline.value.querySelector(".fullTitle");
  const first = bodyInner.value.querySelector(".abstract") || bodyInner.value;
  const full = parseFloat(getComputedStyle(title).marginTop) + getCapOffset(title);
  return {full: Math.round(full), side: Math.round(TEXT_BLOCK / 2 + getCapOffset(first))};
}
// the website's and VIZBI's group's widths on one line each; their height in a row, and one
// under the other
function measureFooter() {
  const element = references.value;
  const items = [".link", ".ours"].map((name) => element.querySelector(name));
  const [left, right] = items.map((item) => Math.ceil(item?.scrollWidth || 0));
  const centre = 0;
  const rowHeight = Math.max(...items.map((item) => item?.offsetHeight || 0));
  Object.assign(element.style, {display: "flex", flexDirection: "column"});
  const columnHeight = element.offsetHeight;
  Object.assign(element.style, {display: "", flexDirection: ""});
  return {left, centre, right, gap: 16, rowHeight, columnHeight};
}
// the page open at least the window's height below its top, less main's padding below
function getMinHeight() {
  const main = page.value.closest("main") || document.body;
  const top = page.value.getBoundingClientRect().top + window.scrollY;
  return Math.floor(window.innerHeight - top - parseFloat(getComputedStyle(main).paddingBottom));
}
// the window's width less the page's margins (main's padding), and its height below the toolbar
// less the margin below the page
function getRoom() {
  const main = page.value.closest("main") || document.body;
  const {paddingLeft, paddingBottom} = getComputedStyle(main);
  const margin = parseFloat(paddingLeft) || 0;
  const top = header.value.getBoundingClientRect().top + window.scrollY;
  const height = window.innerHeight - top - header.value.offsetHeight - parseFloat(paddingBottom);
  return {width: document.documentElement.clientWidth - 2 * margin, height};
}
// the layout that shows it all, and the poster largest (posterLayout.js), and its widths
function updateLayout() {
  if (!header.value?.offsetWidth || !headline.value || !poster.value) return; // hidden: full screen
  const character = measureCharacter();
  const chosen = getPosterLayout({
    ...getRoom(),
    aspect: getAspect(poster.value),
    toPx: (characters) => Math.round(characters * character),
    measureHead,
    measureBody,
    imageTop: getImageTops(),
    footer: measureFooter(),
    isNarrow: displayWidth.value === "narrow",
    current: layoutKey,
  });
  layoutKey = getKey(chosen);
  [isFooterRow.value, imageTop.value] = [chosen.isFooterRow, chosen.imageTop || 0];
  chosenHeight.value = chosen.image.height + 1; // a pixel's rounding
  minHeight.value = getMinHeight();
  [layout.value, textColumn.value] = [chosen.layout, chosen.textColumn];
  [contentWidth.value, closedWidth.value] = [chosen.contentWidth, chosen.closedWidth];
}
function onResize() {
  updateLayout();
  updateViewerHeight();
}

// the headline grows and shrinks as Details opens and closes, and the poster's column as the
// text's opens beside it (and both as the window changes): the image follows on every frame.
// The page's own width (a scroll bar coming or going) chooses the layout again
let [sizeObserver, pageObserver, pageWidthSeen] = [null, null, 0];
function onPageResize() {
  const width = document.documentElement.clientWidth;
  if (width === pageWidthSeen) return;
  pageWidthSeen = width;
  onResize();
}
function observeSizes() {
  [sizeObserver, pageObserver].forEach((observer) => observer?.disconnect());
  if (!header.value) return;
  sizeObserver = new ResizeObserver(() => updateViewerHeight());
  [header.value, headline.value.parentElement, viewerSlot.value].forEach((element) => {
    sizeObserver.observe(element);
  });
  pageObserver = new ResizeObserver(onPageResize);
  pageObserver.observe(document.documentElement);
}

watchEffect(async () => {
  details.value = null;
  try {
    details.value = await fetchPosterDetails(props.year, props.posterNum);
  } catch (error) {
    console.error(error); // the page without the abstract and link
  }
});
function onPosterShown() {
  onResize();
  observeSizes();
}
watch(poster, () => nextTick(onPosterShown));
watch(details, () => nextTick(onResize)); // the abstract's height, once it has arrived
onMounted(() => {
  noteGallery();
  nextTick(onPosterShown);
  document.fonts?.ready.then(onResize); // the text's measures (its ch, the headline) in its font
  window.addEventListener("resize", onResize);
});
onActivated(noteGallery);
onBeforeUnmount(() => {
  [sizeObserver, pageObserver].forEach((observer) => observer?.disconnect());
  window.removeEventListener("resize", onResize);
});
</script>

<style scoped>
.posterPage {
  /* its layout's width (inline, posterLayout.js), up to the window's less Main.vue's padding:
     App.vue's body, sized by its content, is set free on this page (global.css) */
  width: calc(100vw - 2rem);
  max-width: 100%;
  margin-inline: auto;
  transition: width var(--unfold) ease;
  overflow-wrap: anywhere; /* a long address (e.g. the author's link) wraps on a phone */
  --side: 2.75rem; /* the toolbar's two ends, equal: the heading centred on the page */
  /* the poster's one surface: toolbar, details, abstract and around the image, open or closed;
     a step darker than the page (PosterViewer.vue's box while it loads) */
  --poster-surface: #ececee;
  --hover-text: black; /* hover: a little brighter, never green (green: chosen, open) */
  --hover-background: var(--filter-control-background);
  --title-color: var(--text-color);
  /* the collection's green: on white, darker for contrast (5:1; the brand green is 2:1) */
  --collection-color: color-mix(in srgb, var(--brand-hover-color) 70%, black);
  --unfold: 0.35s;
}
/* dark mode (the body's class); the whole selector global: Vue would keep only .darkMode */
:global(.darkMode .posterPage) {
  --poster-surface: #161719;
  --hover-text: white;
  --hover-background: transparent;
  --title-color: white;
  --collection-color: var(--brand-color); /* 7.9:1 on the dark page */
}
@media (prefers-reduced-motion: reduce) {
  .posterPage {
    --unfold: 0s;
  }
}
.status {
  color: #777;
}
/* the page's grid: the toolbar across; then, stacked, the headline, the poster and the body
   (abstract, references) one under the other; side, the headline across and the body left of the
   poster; full, the headline and the body left of the poster, which starts under the toolbar
   (its last row takes what the poster's height leaves). The text column eases open from 0 wide
   as Details opens */
.poster {
  cursor: pointer; /* all of it opens and closes Details; the image opens full screen */
  background: var(--poster-surface);
  display: grid;
  grid-template-columns: 0px 0px minmax(0, 1fr);
  grid-template-areas: "header header header" "head head head" "viewer viewer viewer"
    "body body body" ". . ." "footer footer footer";
  grid-template-rows: auto auto auto auto 1fr auto;
  transition: grid-template-columns var(--unfold) ease;
}
.poster.side {
  grid-template-areas: "header header header" "head head head" "body . viewer" ". . viewer"
    "footer . viewer";
  grid-template-rows: auto auto auto 1fr auto;
}
.poster.full {
  grid-template-areas: "header header header" "head . viewer" "body . viewer" ". . viewer"
    "footer . viewer";
  grid-template-rows: auto auto auto 1fr auto;
}
/* beside the poster, the links in a row under both poster and text (posterLayout.js: where
   the window has the room) */
.side.hasFooterRow {
  grid-template-areas: "header header header" "head head head" "body . viewer" ". . viewer"
    "footer footer footer";
}
.full.hasFooterRow {
  grid-template-areas: "header header header" "head . viewer" "body . viewer" ". . viewer"
    "footer footer footer";
}
/* open, at least the window's height: the row above the footer takes the rest (nothing
   stretched), so the links sit at the bottom */
.poster.isOpen {
  min-height: var(--min-height);
}
.side.isOpen,
.full.isOpen {
  grid-template-columns: calc(var(--text-pad) + var(--text-column)) var(--gutter) minmax(0, 1fr);
}
.header {
  grid-area: header;
}
.viewerSlot {
  grid-area: viewer;
  align-self: start; /* top-aligned; a longer abstract beside it doesn't stretch it */
  min-width: 0;
  padding: 0 var(--inset) var(--inset); /* the surface either side and below the image */
}
/* open, beside the text: its top level with the text's first capitals (the title's, full; the
   abstract's, side: posterLayout.js), eased with the unfold */
.viewerSlot {
  transition: padding-top var(--unfold) ease;
}
.full.isOpen .viewerSlot,
.side.isOpen .viewerSlot {
  padding-top: var(--image-top);
}
/* open, stacked: a poster narrower than its text (shrunk so all of it fits) on black either
   side, exactly its height (the viewer's own row: PosterViewer.vue's .stage), as a viewer's
   pillarbox; its size and controls unchanged */
.stacked.isOpen .viewerSlot :deep(.stage),
.columns.isOpen .viewerSlot :deep(.stage) {
  background: #000;
}
/* open, below it the text: the poster no wider than the text, their edges aligned */
.stacked.isOpen .viewerSlot,
.columns.isOpen .viewerSlot {
  padding-inline: var(--text-pad);
}
.toolbar {
  display: grid;
  grid-template-columns: var(--side) minmax(0, 1fr) var(--side);
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.5rem 0.5rem;
}
.back {
  justify-self: start;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 4px;
  color: var(--text-color);
}
.back:hover {
  color: var(--hover-text);
  background: var(--hover-background);
}
.back svg {
  width: 1.5rem;
  height: 1.5rem;
  fill: currentColor;
}
.heading {
  display: flex;
  justify-content: center;
  gap: 0.6em;
  min-width: 0;
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.35;
  white-space: nowrap;
}
/* open: the collection beside Back */
.isOpen .heading {
  justify-content: flex-start;
}
/* green: the event, collection and number (the site's colour for the current context) */
.collection {
  flex: none;
  color: var(--collection-color);
  font-weight: 700;
}
/* the title and authors: one region, cut short at its end */
.text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
.title {
  color: var(--title-color);
  font-weight: 700;
  margin-right: 0.35em; /* with the space after it: the gap before the authors */
}
.authors {
  font-weight: 400;
  color: color-mix(in srgb, var(--text-color) 60%, var(--background-color));
}
/* the chevron: neutral, a little brighter on hover, green while open (the light theme's darker
   green: 5:1 on white); a generous target round a small icon (global.css styles every
   button: reset here) */
.detailsChevron {
  justify-self: end;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.25rem;
  padding: 0;
  border: none;
  border-radius: 4px;
  box-shadow: none;
  background: transparent;
  color: var(--text-color);
  cursor: pointer;
}
.detailsChevron:hover {
  color: var(--hover-text);
  background: var(--hover-background);
}
.detailsChevron[aria-expanded="true"] {
  color: var(--collection-color);
}
.detailsChevron svg {
  width: 1rem;
  height: 1rem;
}
/* unfolding: its row eased from 0 to its content's height, and back (the image follows); the
   body below the poster the same way */
.expansion {
  grid-area: head;
  min-width: 0;
}
.expansion,
.stacked .body,
.columns .body,
.stacked .footer,
.columns .footer {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows var(--unfold) ease;
}
.isOpen .expansion,
.stacked.isOpen .body,
.columns.isOpen .body,
.stacked.isOpen .footer,
.columns.isOpen .footer {
  grid-template-rows: 1fr;
}
/* all of it, in the page's flow: never cut, never scrolling on its own (closed: hidden by
   its row's 0 height) */
/* their widths without their padding: the text column's (posterLayout.js) */
.expansionInner,
.bodyInner {
  box-sizing: content-box;
}
.expansionInner {
  min-height: 0;
  overflow: hidden;
  padding: 0 0.75rem;
  font-size: 0.95rem;
}
.isOpen .expansionInner {
  padding-bottom: 0.75rem;
}
.expansionInner p {
  margin: 0 0 0.5rem;
}
.body {
  grid-area: body;
  min-width: 0;
  overflow: hidden;
}
.bodyInner {
  min-width: 0; /* a long link (cut short, …) mustn't widen it */
  min-height: 0;
  padding: 0 0.75rem;
  font-size: 0.95rem;
}
.stacked.isOpen .bodyInner,
.columns.isOpen .bodyInner {
  padding-block: 0.75rem;
}
/* the abstract's lines no longer than its column (70 characters) across the page */
.stacked .bodyInner {
  max-width: var(--text-column);
}
/* columns: two of them, balanced, down the first then the second; the references below */
.columns .bodyInner {
  max-width: calc(2 * var(--text-column) + var(--column-gap));
}
.columns .abstract {
  column-count: 2;
  column-gap: var(--column-gap);
}
/* beside the poster: its own width, revealed as the column opens; closed, no height (else the
   text, folded into no width, would make the row tall) */
.side .bodyInner,
.full .bodyInner,
.full .expansionInner {
  width: var(--text-column);
  padding: 0.75rem 0 0.75rem 0.75rem;
}
.full .expansionInner {
  padding-block: 0;
}
.side:not(.isOpen) .body,
.full:not(.isOpen) .body,
.side:not(.isOpen) .footer,
.full:not(.isOpen) .footer {
  max-height: 0;
}
.footer {
  grid-area: footer;
  min-width: 0;
  overflow: hidden;
}
.footerInner {
  box-sizing: content-box;
  min-width: 0;
  min-height: 0;
  padding: 0 0.75rem;
  font-size: 0.95rem;
}
.isOpen .footerInner {
  padding-bottom: 0.75rem;
}
.stacked .footerInner {
  max-width: var(--text-column);
}
.columns .footerInner {
  max-width: calc(2 * var(--text-column) + var(--column-gap));
}
.side .footerInner,
.full .footerInner {
  width: var(--text-column);
  padding-right: 0;
}
/* the row across the page: the address's end at the poster's right edge */
.side.hasFooterRow .footerInner,
.full.hasFooterRow .footerInner {
  width: auto;
  padding-right: var(--inset);
}
.abstract {
  margin: 0;
}
/* the open header, as a paper's abstract: a larger title with room above it, the authors on
   their own line, their addresses close below */
.fullTitle {
  margin: 0.9rem 0 0.35rem;
  font-size: 1.55rem;
  line-height: 1.25;
  color: var(--title-color);
}
.fullAuthors {
  color: color-mix(in srgb, var(--text-color) 70%, var(--background-color));
  font-size: 1.05rem;
}
.expansionInner .fullAuthors {
  margin-bottom: 0.15rem;
}
.affiliation {
  color: color-mix(in srgb, var(--text-color) 55%, var(--background-color));
}
.expansionInner .affiliation {
  margin-bottom: 0.9rem;
}
/* the links: the author's website, then VIZBI's group (this poster and its talk) under it; or
   a row, the website left and the group right (posterLayout.js: where it fits). All at one
   size, the talk's */
.references {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.4rem;
  margin-top: 0.75rem;
  line-height: 1.4;
}
.references.isRow {
  flex-direction: row;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
}
/* a long website cut short (…) to its space, never into the group */
.link {
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.isRow .link {
  flex: 0 1 auto;
}
.ours {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0 0.5em;
  white-space: nowrap;
}
.isRow .ours {
  flex: none;
  margin-left: auto; /* right, with or without a website */
}
/* this poster: green, where you are (not a link) */
.current {
  color: var(--collection-color);
}
.dot {
  color: color-mix(in srgb, var(--text-color) 45%, var(--background-color));
}
/* its talk: the text's colour (white, dark), a link */
.talk {
  color: var(--title-color);
  text-decoration: none;
}
.talk:hover {
  text-decoration: underline;
  text-underline-offset: 0.2em;
}
a:focus-visible,
button:focus-visible {
  outline: 2px solid var(--brand-color);
  outline-offset: 2px;
}
</style>
